import { useState, useEffect, useLayoutEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { settingsApi } from '../api/client';
import type { AppSettings, SenderAddress, ShippingOptions, NotificationSettings, LabelFormat } from '../types';
import { US_STATES, CANADIAN_PROVINCES } from '../constants/regions';
import {
  LABEL_SIZE_OPTIONS,
  normalizeLabelCompatibility,
  defaultSenderAddress,
  defaultShippingOptions,
  defaultNotifications,
} from '../constants/settingsOptions';
import { type SettingsSection } from '../components/settings/SettingsTabs';
import PageHeader from '../components/PageHeader';
import SenderAddressSection from '../components/settings/SenderAddressSection';
import ShippingOptionsSection from '../components/settings/ShippingOptionsSection';
import NotificationsSection from '../components/settings/NotificationsSection';

function SettingsPage() {
  const { toasts, removeToast, success, error } = useToast();

  // Form state
  const [senderAddress, setSenderAddress] = useState<SenderAddress>(defaultSenderAddress);
  const [shippingOptions, setShippingOptions] = useState<ShippingOptions>(defaultShippingOptions);
  const [notifications, setNotifications] = useState<NotificationSettings>(defaultNotifications);

  // UI state
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<SettingsSection>('sender');

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    // Seed-a-form pattern: still fetch fresh on each mount (default staleTime),
    // so revisiting after a save shows the saved values — but don't
    // background-refetch on reconnect (window-focus refetch is already off in
    // the query client, and there's no interval here). A background refetch
    // mid-edit could replace the settings under the user's unsaved edits, or
    // (on failure) swap in the full-screen error view and discard them.
    refetchOnReconnect: false,
  });
  const loading = settingsQuery.isLoading;
  // Gate on the initial-load error only, so a (now-disabled) background refetch
  // failure could never replace the form.
  const loadError = settingsQuery.isLoadingError
    ? settingsQuery.error instanceof Error
      ? settingsQuery.error.message
      : 'Failed to load settings'
    : '';

  // Seed the editable form state once the settings load (or reload). A layout
  // effect runs before paint, so the form never flashes its default values in
  // the frame between the data arriving and the state being seeded.
  useLayoutEffect(() => {
    const settings = settingsQuery.data;
    if (!settings) return;
    if (settings.sender_address) setSenderAddress(settings.sender_address);
    if (settings.shipping_options) setShippingOptions(normalizeLabelCompatibility(settings.shipping_options));
    if (settings.notifications) setNotifications(settings.notifications);
  }, [settingsQuery.data]);

  // Surface a load failure as a toast, once per distinct failure. `error` is
  // deliberately not a dependency so a stable failure doesn't re-toast on
  // every render.
  useEffect(() => {
    if (settingsQuery.isLoadingError) {
      const message =
        settingsQuery.error instanceof Error ? settingsQuery.error.message : 'Failed to load settings';
      error('Settings Unavailable', message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.isLoadingError]);

  const handleSave = async () => {
    // Validation
    if (!senderAddress.name || !senderAddress.street1 || !senderAddress.city || !senderAddress.state || !senderAddress.zip) {
      error('Missing Required Fields', 'Please fill in all required sender address fields.');
      return;
    }

    if (senderAddress.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderAddress.email)) {
      error('Invalid Email', 'Enter a valid sender email address.');
      return;
    }

    if (shippingOptions.insurance_enabled && shippingOptions.default_insurance_amount <= 0) {
      error('Invalid Insurance Amount', 'Enter an insurance amount greater than zero.');
      setActiveSection('shipping');
      return;
    }

    setSaving(true);
    try {
      const settings: AppSettings = {
        sender_address: senderAddress,
        shipping_options: shippingOptions,
        notifications: notifications,
      };
      await settingsApi.update(settings);
      success('Settings Saved', 'Your settings have been updated successfully.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      error('Save Failed', message);
    } finally {
      setSaving(false);
    }
  };

  const handleSenderChange = (field: keyof SenderAddress, value: string) => {
    setSenderAddress((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'country' && value !== prev.country ? { state: '' } : {}),
    }));
  };

  const handleShippingChange = <K extends keyof ShippingOptions>(field: K, value: ShippingOptions[K]) => {
    setShippingOptions((prev) => {
      const updated = { ...prev, [field]: value };

      // Auto-adjust label size when format changes
      if (field === 'label_format') {
        const format = value as LabelFormat;
        const currentSizeOption = LABEL_SIZE_OPTIONS.find((opt) => opt.value === prev.label_size);
        if (currentSizeOption && !currentSizeOption.formats.includes(format)) {
          // Find a compatible size for the new format
          const compatibleSize = LABEL_SIZE_OPTIONS.find((opt) => opt.formats.includes(format));
          if (compatibleSize) {
            updated.label_size = compatibleSize.value;
          }
        }
      }

      return updated;
    });
  };

  const handleNotificationChange = (field: keyof NotificationSettings, value: string | boolean) => {
    setNotifications((prev) => ({ ...prev, [field]: value }));
  };

  const availableRegions = senderAddress.country === 'CA' ? CANADIAN_PROVINCES : US_STATES;

  const SECTIONS: { id: SettingsSection; label: string }[] = [
    { id: 'sender', label: 'Sender address' },
    { id: 'shipping', label: 'Shipping options' },
    { id: 'notifications', label: 'Notifications' },
  ];

  if (loading) {
    return (
      <div className="settings-page">
        <div className="panel" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="skeleton skeleton-heading" style={{ width: '200px' }} />
          <div className="skeleton skeleton-text" style={{ width: '100%', height: '300px', marginTop: '20px' }} />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <>
        <ToastContainer toasts={toasts} onClose={removeToast} />
        <div className="settings-page">
          <div className="panel" style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '12px' }}>Settings Could Not Be Loaded</h2>
            <p role="alert" style={{ color: 'var(--error-text)', marginBottom: '20px' }}>{loadError}</p>
            <p style={{ color: 'var(--aged-mahogany-light)', marginBottom: '20px' }}>
              Saving is disabled to prevent temporary defaults from overwriting your stored configuration.
            </p>
            <button type="button" className="btn btn-primary" onClick={() => settingsQuery.refetch()}>
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="settings-page">
        <PageHeader
          title="Settings"
          description="Sender address, shipping defaults, and notifications for this workspace"
        />

        <div className="settings-grid">
          <div className="settings-vnav" role="tablist" aria-label="Settings sections">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                type="button"
                role="tab"
                aria-selected={activeSection === section.id}
                className={activeSection === section.id ? 'active' : ''}
                onClick={() => setActiveSection(section.id)}
              >
                {section.label}
              </button>
            ))}
          </div>

          <div className="settings-card">
            <div className="settings-content">
              {activeSection === 'sender' && (
                <SenderAddressSection
                  address={senderAddress}
                  regions={availableRegions}
                  onChange={handleSenderChange}
                />
              )}
              {activeSection === 'shipping' && (
                <ShippingOptionsSection options={shippingOptions} onChange={handleShippingChange} />
              )}
              {activeSection === 'notifications' && (
                <NotificationsSection notifications={notifications} onChange={handleNotificationChange} />
              )}
            </div>

            <div className="settings-actions">
              <button
                className={`btn btn-primary ${saving ? 'loading' : ''}`}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SettingsPage;
